import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { appInsightsService } from "./services/appInsights.service";
import { globalErrorHandler } from "./services/globalErrorHandler.service";
import { signalAppReady } from "./utils/appLoadingSignal";
import { useAppInitialization } from "./hooks/useAppInitialization";
import { useTenantCreation } from "./hooks/useTenantCreation";
import { useMsalAuth } from "./hooks/useMsalAuth";
import { loadUserPreferences } from "./services/userPreferencesService";
import { authService } from "./utils/authService";
import { withInstanceIdParam } from "./utils/instanceId";
import { useSkyTheme } from "@skypointcloud/skypoint-ui";
import { fetchAccessibleInstanceIds } from "./common/App.service";
import useWindowDimensions from "./common/useWindowDimensions";
import { HeaderBanner } from "./components/HeaderBanner";
import ErrorPage from "./components/shared/errorpage";
import { AppLayout } from "./components/AppLayout";
import { AppLoadingStates } from "./components/app/AppLoadingStates";
import { IRootReducerState } from "./store/IRootReducer";
import { AuthenticatedAnalyticsTracker } from "./components/AnalyticsProvider";
import { logger } from "./logger-init";

const reactPlugin = appInsightsService.initialize();

// Initialize global error handlers
globalErrorHandler.initialize();

// Performance timing for login-to-landing measurement
const LOGIN_PERF_KEY = "__SKY_LOGIN_PERF__";

function AppNew() {
  const { currentUser, getQueryString } = useMsalAuth();
  const { setMode } = useSkyTheme();

  const authState = useSelector((state: IRootReducerState) => state.authReducer);
  const { isError, errorMessage, errorCode } = authState.error;

  // Start performance timing on mount
  useEffect(() => {
    if (!(window as any)[LOGIN_PERF_KEY]) {
      (window as any)[LOGIN_PERF_KEY] = {
        startTime: performance.now(),
        appMounted: Date.now(),
      };
    }
  }, []);

  const {
    isAppLoaded,
    isCheckResourceFailed,
    isShowCreateTenant,
    createOwnInfrastructure,
    showExternalTenantCreationError,
    instanceId,
    userAccesstoInstance,
    accessDeniedReason,
    hideNewdashboard,
    setInstanceId,
    checkIfResourceExist,
    hasAccessToInstance,
    getUserAccess,
    hideExternalTenantCreationError,
    setIsShowCreateTenant,
    setCreateOwnInfrastructure,
    setShowExternalTenantCreationError,
    setIsCheckResourceFailed,
    setUserAccessToInstance,
  } = useAppInitialization({
    currentUserId: currentUser?.id,
    getQueryString,
  });

  const [isSetupFinished, setIsSetupFinished] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");

  const { createResourceForTenant, createOwnInfrastructureFn } = useTenantCreation({
    instanceId,
    getQueryString,
    setIsShowCreateTenant,
    setCreateOwnInfrastructure,
    setShowExternalTenantCreationError,
    setIsCheckResourceFailed,
    setNotificationMessage,
    setIsSetupFinished,
    getUserAccess,
    checkIfResourceExist,
  });

  const { windowWidth } = useWindowDimensions();

  useEffect(() => {
    document.body.classList.remove("hold-transition");
  }, []);

  // Signal startup overlay to dismiss once app is fully loaded
  useEffect(() => {
    if (isAppLoaded) signalAppReady();
  }, [isAppLoaded]);

  // Log performance when app finishes loading
  useEffect(() => {
    if (isAppLoaded && (window as any)[LOGIN_PERF_KEY]) {
      const perf = (window as any)[LOGIN_PERF_KEY];
      const loadTime = performance.now() - perf.startTime;
      logger.debug(`[Performance] App loaded in ${loadTime.toFixed(0)}ms`);
      // Clear to avoid duplicate logs
      delete (window as any)[LOGIN_PERF_KEY];
    }
  }, [isAppLoaded]);

  useEffect(() => {
    const initializeApp = async () => {
      // Get URL instanceId first
      const params = new URLSearchParams(window.location.search);
      const urlInstanceId = params.get("instanceid") || "";

      if (urlInstanceId) {
        // URL has instanceId - use it directly (active session navigation)
        // Persist to sessionStorage so it survives client-side navigations
        // that strip the query param (e.g., React Router route changes)
        authService.saveInstanceId(urlInstanceId);
        setInstanceId(urlInstanceId);
        hasAccessToInstance(urlInstanceId);
        // Apply saved theme for active session
        const savedTheme = authService.getSavedTheme();
        setMode(savedTheme.toLowerCase() as "light" | "dark");
        return;
      }

      // No URL instanceId - load preferences (sessionStorage for active session, DB for fresh login)
      const preferences = await loadUserPreferences();
      // Apply theme from loaded preferences (defaults to light)
      const theme = (preferences?.theme ?? "Light").toLowerCase() as "light" | "dark";
      setMode(theme);
      const savedInstanceId = preferences?.instanceId || null;

      if (savedInstanceId) {
        // Update URL with saved instanceId (without full page reload)
        const pathname = window.location.pathname || "/";
        window.history.replaceState(
          {},
          "",
          withInstanceIdParam(pathname, window.location.search, savedInstanceId),
        );
        setInstanceId(savedInstanceId);
        hasAccessToInstance(savedInstanceId);
      } else {
        // No instanceId anywhere — fetch user's accessible instances (retry up to 3 times)
        let lastError: unknown;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const accessibleIds = await fetchAccessibleInstanceIds();
            if (accessibleIds.length > 0) {
              const targetId = accessibleIds[0];
              authService.saveInstanceId(targetId);
              const pathname = window.location.pathname || "/";
              window.history.replaceState(
                {},
                "",
                withInstanceIdParam(pathname, window.location.search, targetId),
              );
              setInstanceId(targetId);
              hasAccessToInstance(targetId);
              return;
            }
            // API succeeded but returned 0 instances — user genuinely has no access
            setUserAccessToInstance(false);
            return;
          } catch (err) {
            lastError = err;
            logger.warn(`[App] fetchAccessibleInstanceIds attempt ${attempt + 1} failed`, {
              error: err instanceof Error ? err.message : String(err),
            });
            if (attempt < 2) await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
          }
        }
        // All retries failed — show no-access as fallback
        logger.error(
          "[App] All fetchAccessibleInstanceIds attempts failed",
          lastError instanceof Error ? lastError : undefined,
        );
        setUserAccessToInstance(false);
      }
    };

    initializeApp();
  }, []);

  const renderAppContent = () => {
    if (!isAppLoaded) {
      return (
        <AppLoadingStates
          userAccesstoInstance={userAccesstoInstance}
          accessDeniedReason={accessDeniedReason}
          showExternalTenantCreationError={showExternalTenantCreationError}
          isCheckResourceFailed={isCheckResourceFailed}
          isShowCreateTenant={isShowCreateTenant}
          createOwnInfrastructure={createOwnInfrastructure}
          isSetupFinished={isSetupFinished}
          hideExternalTenantCreationError={hideExternalTenantCreationError}
          createResourceForTenant={createResourceForTenant}
          createOwnInfrastructureFn={createOwnInfrastructureFn}
        />
      );
    }

    if (isError) {
      return (
        <ErrorPage
          show={true}
          message={errorMessage}
          errorCode={errorCode}
          title="Warning!"
        />
      );
    }

    return (
      <AppLayout
        windowWidth={windowWidth}
        notificationMessage={notificationMessage}
        hideNewdashboard={hideNewdashboard}
      />
    );
  };

  return (
    <>
      <AuthenticatedAnalyticsTracker
        userId={currentUser?.id}
        email={currentUser?.emails}
      />
      {isAppLoaded && !isError && <HeaderBanner />}
      {renderAppContent()}
    </>
  );
}

export default withAITracking(reactPlugin, AppNew);
